import React from 'react';
import cx from 'classnames';
import { parse, stringify } from 'query-string';
import withAppLayout from '../components/appLayout';
import Head from 'next/head';

import styles from '../styles/login.scss';
import FormBox from '../components/FormBox';
import { Api } from '../api';
// @ts-ignore
import { Router } from '../configs/routes';
import { CDN_CLRSAVE_URL } from '../constants';
import { showError } from '../utils';
import { trackAction, trackPixel } from '../utils/trackActions';
import CUSTOM_ERROR_CODES from '../constants/errorCodes';
import { redirectToLastRoute } from '../utils/commonUtils';
import mailcheck from 'mailcheck';

const api = new Api();
const otpTimeoutDuration = 3000;
const domains = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'rediffmail.com',
  'outlook.com',
  'yahoo.in',
  'yahoo.co.in',
  'aol.com',
  'live.com',
  'msn.com',
];

interface Props {
  userInfo?: any;
  url?: any;
}

interface State {
  isFetching: boolean;
  step: string;
  email: string;
  phone: string;
  otp: string;
  password: string;
  wrongOtp: string;
  wrongPassword: string;
  noUserError: string;
  megaError: string;
  otpTimeout: any;
  resendCount: number;
  startClicksCount: number;
  emailValidationStatus: string;
}

class Login extends React.Component<Props, State> {
  constructor(props: any) {
    super(props);
    const queries = props.url.query;
    this.state = {
      isFetching: false,
      step: queries.mode || 'email',
      email: queries.contact || '',
      phone: queries.contact && !isNaN(queries.contact) ? queries.contact : '',
      otp: '',
      password: '',
      wrongOtp: null,
      wrongPassword: null,
      noUserError: null,
      megaError: null,
      otpTimeout: null,
      resendCount: 0,
      startClicksCount: 0,
      emailValidationStatus: null,
    };
  }

  public componentDidMount() {
    const { step, email } = this.state;
    if (step === 'otp' && email) {
      this.resendOtp();
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: any) {
    const { mode } = nextProps.url.query;
    const oldMode = this.props.url.query.mode;
    if (oldMode !== mode) {
      this.setState({
        step: mode || 'email',
      });
    }
  }

  public checkEmailValidity = () => {
    const { email } = this.state;
    let response = null;
    mailcheck.run({
      domains: [...domains],
      email,
      suggested: suggestion => {
        if (suggestion.full !== email) {
          response = suggestion;
        }
      },
      empty: () => {
        const mailDomain = email.split('@')[1];
        if (domains.indexOf(mailDomain) !== -1) {
          response = {
            address: email.split('@')[0],
            domain: mailDomain,
            full: email,
          };
        } else {
          response = {
            address: email.split('@')[0],
            domain: mailDomain,
            full: 'unknown',
          };
        }
      },
    });
    return response;
  };

  public actionAfterEmailValidation = () => {
    const { email, startClicksCount } = this.state;

    const response = this.checkEmailValidity();
    if (
      response !== null &&
      response.full !== email &&
      response.full !== 'unknown'
    ) {
      this.setState({
        emailValidationStatus: 'invalid',
      });
      return false;
    } else if (response.full === 'unknown' && startClicksCount < 1) {
      this.setState({
        startClicksCount: startClicksCount + 1,
        emailValidationStatus: response.full,
      });
      return false;
    } else if (response.full === email) {
      this.setState({
        emailValidationStatus: 'valid',
      });
    }
    return true;
  };

  public submitEmail = async (e: any) => {
    e.preventDefault();
    const { email } = this.state;

    if (email) {
      const isPhoneNumber = !isNaN(email);
      if (isPhoneNumber) {
        this.setState({ phone: email });
      }
      this.setState({ isFetching: true, otpTimeout: {} });
      api
        .findUser(email)
        .then(response => {
          if (response.externalId) {
            if (isPhoneNumber) {
              trackAction('send-otp', 'authentication');
              api
                .sendOtp(email)
                .then(() => {
                  const queries = parse(location.search);
                  queries.mode = 'otp';
                  queries.contact = email;
                  const search = stringify(queries);
                  const path = `${location.pathname}?${search}`;
                  Router.pushRoute(path, path, { shallow: true });
                  this.setState({ isFetching: false });
                })
                .catch(() => {
                  this.setState({
                    isFetching: false,
                    otpTimeout: setTimeout(() => {
                      this.setState({
                        otpTimeout: null,
                      });
                    }, otpTimeoutDuration),
                  });
                });
            } else {
              const queries = parse(location.search);
              queries.mode = 'login';
              queries.contact = email;
              const search = stringify(queries);
              const path = `${location.pathname}?${search}`;
              Router.pushRoute(path, path, { shallow: true });
              this.setState({ isFetching: false });
            }
          } else {
            if (isPhoneNumber) {
              this.setState({
                megaError: 'True',
                isFetching: false,
                step: 'email',
              });
              trackAction(
                'login-failed',
                'authentication',
                'phone-number-not-registered'
              );
            } else {
              const queries = parse(location.search);
              queries.mode = 'register';
              queries.contact = email;
              const search = stringify(queries);
              const path = `${location.pathname}?${search}`;
              const validEmail = this.actionAfterEmailValidation();
              if (!validEmail) {
                this.setState({ isFetching: false });
                return;
              }
              Router.pushRoute(path, path, { shallow: true });
              this.setState({ isFetching: false });
            }
          }
        })
        .catch(error => {
          if (error.status === 400) {
            this.setState({
              megaError: '',
              isFetching: false,
            });
            error
              .json()
              .then(erro =>
                showError(
                  erro.customErrorCode
                    ? CUSTOM_ERROR_CODES[erro.customErrorCode]
                    : erro.errorMessage
                )
              );
          } else {
            this.setState({ megaError: null, isFetching: false });
          }
        });
    }
  };

  public fetchGoogleUrl = (e: any) => {
    e.preventDefault();
    trackAction('google-auth', 'authentication');
    const lastRoute = (parse(location.search).refer || '').replace(
      /^(https?\:\/\/[a-z.-]+)\/?/g,
      '/'
    );
    api
      .getGoogleAuthCallback({ redirect: lastRoute })
      .then(res => {
        try {
          const redirect_url = JSON.parse(res).redirect_url;
          if (redirect_url) {
            window.location = redirect_url;
          }
        } catch (err) {
          throw err;
        }
      })
      .catch(err => {
        throw err;
      });
  };

  public resendOtp = (e?: any) => {
    const { phone, isFetching, otpTimeout, resendCount } = this.state;
    let newResendCount = 0;
    if (e) {
      e.preventDefault();
      newResendCount = resendCount + 1;
    }

    if (isFetching || otpTimeout) {
      return;
    }
    this.setState({ otpTimeout: {}, resendCount: newResendCount });
    trackAction('resend-otp', 'authentication');
    api.sendOtp(phone).then(() => {
      this.setState({
        otpTimeout: setTimeout(() => {
          this.setState({
            otpTimeout: null,
          });
        }, otpTimeoutDuration),
      });
    });
  };

  public register = (e: any) => {
    e.preventDefault();
    const { email, password } = this.state;
    if (email && password) {
      this.setState({ isFetching: true });
      api
        .createAccount({ email, password })
        .then(() => {
          trackPixel('CompleteRegistration');
          redirectToLastRoute('register_with_email');
          trackAction('clearinvest', 'create_your_account');
          this.setState({ isFetching: false });
        })
        .catch(() => {
          // register failed
          trackAction('registration-failed', 'authentication');
          this.setState({ isFetching: false });
        });
    }
  };

  public login = (e: any) => {
    e.preventDefault();
    const { email, password } = this.state;
    if (password) {
      this.setState({ isFetching: true });
      api
        .login({ email, password })
        .then(() => {
          this.setState({ isFetching: false, wrongPassword: null });
          redirectToLastRoute('email_with_password');
        })
        .catch(err => {
          // login failed
          trackAction('login-failed', 'authentication', 'wrong-password');
          if (err.status === 401) {
            this.setState({
              isFetching: false,
              wrongPassword: 'Oops! Wrong password.',
            });
          } else {
            this.setState({
              isFetching: false,
            });
          }
        });
    }
  };

  public submitOTP = (e: any) => {
    e.preventDefault();
    const { otp, email } = this.state;
    if (otp) {
      this.setState({ isFetching: true });
      api
        .submitOtp({ otp, phone: email })
        .then(() => {
          redirectToLastRoute('phone_with_otp');
          this.setState({
            isFetching: false,
            wrongOtp: null,
          });
        })
        .catch(() => {
          trackAction('login-failed', 'authentication', 'wrong-otp');
          this.setState({
            isFetching: false,
            wrongOtp: 'Invalid OTP',
          });
        });
    }
  };

  public editEmail = (e: any) => {
    if (e) {
      e.preventDefault();
    }
    const queries = parse(location.search);
    delete queries.mode;
    const search = stringify(queries);
    const path = `${location.pathname}?${search}`;
    Router.pushRoute(path, path, { shallow: true });
    this.setState({
      megaError: null,
      wrongOtp: null,
      wrongPassword: null,
      noUserError: null,
    });
  };

  public render() {
    const {
      step,
      isFetching,
      email,
      phone,
      otp,
      password,
      wrongOtp,
      wrongPassword,
      noUserError,
      megaError,
      otpTimeout,
      resendCount,
      startClicksCount,
      emailValidationStatus,
    } = this.state;

    return (
      <div className={styles['login-page']}>
        <Head>
          {/* google login script */}
          <script src="https://accounts.google.com/gsi/client" defer />

          {/* <script src="https://apis.google.com/js/platform.js" defer /> */}
          <meta
            name="google-signin-client_id"
            content="YOUR_CLIENT_ID.apps.googleusercontent.com"
          />
        </Head>
        <div className="row">
          <div
            className={cx(
              'large-7 medium-6 small-12 columns',
              styles['login-content']
            )}
          >
            <h4 className={styles.header}>Investing made simple</h4>
            <h5 className={styles.subheader}>
              Easiest Tax saving (80C) &amp; mutual fund investments.
              <br />
              Trusted by over 25 Lakh indians.
            </h5>
            <ul>
              <li className={styles.point}>
                <img
                  className={styles['point-image']}
                  src={`${CDN_CLRSAVE_URL}/business/images/shield.png`}
                  alt="shield.png"
                />
                <label className={styles['point-detail']}>
                  Free, Simple &amp; Safe
                </label>
              </li>
              <li className={styles.point}>
                <img
                  className={styles['point-image']}
                  src={`${CDN_CLRSAVE_URL}/business/images/star.png`}
                  alt="star.png"
                />
                <label className={styles['point-detail']}>
                  Start with just Rs. 500
                </label>
              </li>
              <li className={styles.point}>
                <img
                  className={styles['point-image']}
                  src={`${CDN_CLRSAVE_URL}/business/images/assistance.png`}
                  alt="assistance.png"
                />
                <label className={styles['point-detail']}>
                  Expert picked funds
                </label>
              </li>
            </ul>
          </div>
          <div className={cx('large-5 medium-6 small-12 columns')}>
            <div className={styles['login-box']}>
              <a
                href="/save/login"
                className="full-width ct-btn ct-btn--medium ct-btn--google mb20"
                data-tracking-action="google-login"
                data-tracking-label="Register"
                onClick={this.fetchGoogleUrl}
              >
                Sign in with Google
              </a>
              <p className="ct-or--line">or</p>
              <div className={styles['login-form']}>
                {step === 'email' && [
                  <h6 key="login-signup-label" className="mb20">
                    Login / Sign-up with your Email
                  </h6>,
                  <FormBox
                    key="email-box"
                    name="email"
                    type="email"
                    title="Enter your email"
                    loading={isFetching}
                    onSubmit={this.submitEmail}
                    value={email}
                    error={noUserError}
                    onChange={e =>
                      this.setState({
                        email: e.target.value,
                        startClicksCount: 0,
                      })
                    }
                    megaError={megaError}
                    placeholder="Email address"
                    button={{
                      label: 'Start',
                      loadingLabel: 'Checking',
                    }}
                    emailValidationStatus={emailValidationStatus}
                    emailValidationMessageShown={startClicksCount}
                  />,
                ]}
                {step === 'register' && [
                  <label key="label" className={styles['sub-header']}>
                    {email}
                    <span
                      className={styles['edit-email']}
                      onClick={this.editEmail}
                    >
                      Edit
                    </span>
                  </label>,
                  <FormBox
                    key="formbox"
                    name="register"
                    title="Enter a new password"
                    value={password}
                    onChange={e => this.setState({ password: e.target.value })}
                    loading={isFetching}
                    onSubmit={this.register}
                    type="password"
                    placeholder="Password"
                    email={email}
                    error={wrongPassword}
                    button={{
                      label: 'Create your account',
                      loadingLabel: 'Submitting',
                    }}
                  />,
                ]}
                {step === 'login' && [
                  <label key="label-text">Your Email</label>,
                  <label key="label" className={styles['sub-header']}>
                    {email}
                    <span
                      className={styles['edit-email']}
                      onClick={this.editEmail}
                    >
                      Edit
                    </span>
                  </label>,
                  <FormBox
                    key="login-password"
                    name="login"
                    title="Enter your password"
                    value={password}
                    onChange={e => this.setState({ password: e.target.value })}
                    loading={isFetching}
                    onSubmit={this.login}
                    type="password"
                    error={wrongPassword}
                    forgotPasswordLink
                    email={email}
                    placeholder="Password"
                    button={{
                      label: 'Login',
                      loadingLabel: 'Submitting',
                    }}
                  />,
                  <a
                    key="forgot-password-link"
                    href="/Account/SendResetPasswordLink"
                    className="text-center block small-text bold"
                    onClick={() =>
                      trackAction('forgot-password', 'authentication')
                    }
                  >
                    Forgot your password?
                  </a>,
                ]}
                {step === 'otp' && [
                  <FormBox
                    key="otpbox"
                    name="otp"
                    title={`We have sent an OTP to ${phone}. Please enter it below.`}
                    value={otp}
                    onChange={e => this.setState({ otp: e.target.value })}
                    loading={isFetching}
                    onSubmit={this.submitOTP}
                    error={wrongOtp}
                    placeholder="OTP"
                    button={{
                      label: 'Login',
                      loadingLabel: 'Submitting',
                    }}
                  />,
                  <label
                    key="resend-otp-link"
                    className={cx('text-center block small-text bold', {
                      [styles.disabled]: isFetching || otpTimeout,
                      [styles.hidden]: resendCount >= 3,
                    })}
                  >
                    Didn’t get OTP? <a onClick={this.resendOtp}>Resend OTP</a>
                  </label>,
                  <label
                    key="show-email-link"
                    className="text-center block small-text bold"
                  >
                    Want to sign in with email?{' '}
                    <a onClick={this.editEmail}>Click here</a>
                  </label>,
                ]}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withAppLayout(
  {
    title: 'ClearTax Invest Login',
    description:
      'ClearTax Invest helps you to Invest in the best performing funds in 2021 & let your money grow through the power of compounding. Login and start your investment journey today!',
  },
  Login
);



// WEBPACK FOOTER //
// ./pages/login.tsx